// src/pages/ContactUs.js
import React, { useState, useContext, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NotificationContext } from '../context/NotificationContext';
import { UserContext } from '../context/UserContext';
import { BASE_URL, IS_PRERENDERING } from '../utils/config';
import './ContactUs.css'; // This line is essential and will not be removed.
import { validateEmail } from '../utils/userValidation'; // Import email validation
import { apiFetch } from '../utils/apiClient'; // Import apiFetch
import { useRecaptcha } from '../hooks/useRecaptcha'; // Import the hook

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

const ContactUs = () => {
  const { t } = useTranslation();
  const { addNotification } = useContext(NotificationContext);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const handleVerifyRef = useRef(null); // Create a ref to hold the callback

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // If an error was set from an async callback, throw it during render to be caught by the Error Boundary
  if (error) {
    throw error;
  }

  // Pre-fill form if user is logged in
  useEffect(() => {
    // Only pre-fill if the fields are empty, to avoid overwriting user's manual input.
    setFormData((prev) => {
      const newName = prev.name === '' && user.isLoggedIn ? user.fullName || '' : prev.name;
      const newEmail = prev.email === '' && user.isLoggedIn ? user.email || '' : prev.email;
      if (newName !== prev.name || newEmail !== prev.email) {
        return { ...prev, name: newName, email: newEmail };
      }
      return prev;
    });
  }, [user.isLoggedIn, user.fullName, user.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRecaptchaExpire = useCallback(() => {
    setIsSubmitting(false);
    addNotification(t('recaptcha.expired'), 'warning');
  }, [addNotification, t]);

  const handleRecaptchaError = useCallback(
    (err) => {
      setIsSubmitting(false);
      addNotification(t('recaptcha.loadError'), 'error', 7000);
      setError(new Error(t('recaptcha.loadError'))); // Propagate error
    },
    [addNotification, t, setError]
  );

  const handleReset = useCallback(() => {
    // Reset form, but keep user's name and email if logged in to allow another message
    setFormData({
      name: user.isLoggedIn ? user.fullName : '',
      email: user.isLoggedIn ? user.email : '',
      subject: '',
      message: '',
    });
  }, [user.isLoggedIn, user.fullName, user.email]);

  const { recaptchaRef, execute, onChange, reset, onErrored, onExpired } = useRecaptcha((token) => handleVerifyRef.current(token), handleRecaptchaExpire, handleRecaptchaError);

  const handleVerify = useCallback(
    async (recaptchaToken) => {
      try {
        // The initial notification is now in handleSubmit
        const body = { ...formData, recaptchaToken };

        // Use apiFetch to automatically handle the Authorization header.
        const response = await apiFetch(
          `${BASE_URL}/api/general/contact-us`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
          user.token // Pass the token to apiFetch
        );
        const data = await response.json();

        if (response.ok) {
          addNotification(t('contactUsPage.successMessage', { ticketId: data.ticketId }), 'success', 7000);
          handleReset(); // Use the new reset handler
          navigate('/'); // Navigate to home page on success
        } else {
          reset(); // Reset reCAPTCHA on failure
          throw new Error(data.message || t('contactUsPage.genericError'));
        }
      } catch (error) {
        reset(); // Also reset on network errors
        setError(error);
      } finally {
        setIsSubmitting(false);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData, user, addNotification, reset, navigate, setError, t, handleReset]
  );

  // Keep the ref updated with the latest version of the handler, including `reset`.
  useEffect(() => {
    handleVerifyRef.current = handleVerify;
  }, [handleVerify]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      addNotification(t('contactUsPage.allFieldsRequired'), 'warning');
      return;
    }

    // Email validation
    const emailError = validateEmail(formData.email);
    if (emailError) {
      addNotification(emailError, 'error');
      return;
    }

    if (IS_PRERENDERING) {
      addNotification(t('contactUsPage.prerendering'), 'info');
      return;
    }

    setIsSubmitting(true);
    addNotification(t('contactUsPage.sending'), 'info');
    execute();
  };

  return (
    <div className="contact-us-page-container">
      <div className="contact-us-container">
        <h2>{t('contactUsPage.title')}</h2>
        <p>{t('contactUsPage.description')}</p>
        <p className="mandatory-note">{t('contactUsPage.mandatoryNote')}</p>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="name">
              {t('contactUsPage.nameLabel')}
              <span className="required-asterisk">*</span>
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required disabled={isSubmitting} autoComplete="name" />
          </div>
          <div className="form-group">
            <label htmlFor="email">
              {t('contactUsPage.emailLabel')}
              <span className="required-asterisk">*</span>
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required disabled={isSubmitting} autoComplete="email" />
          </div>
          <div className="form-group">
            <label htmlFor="subject">
              {t('contactUsPage.subjectLabel')}
              <span className="required-asterisk">*</span>
            </label>
            <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required disabled={isSubmitting} />
          </div>
          <div className="form-group">
            <label htmlFor="message">
              {t('contactUsPage.messageLabel')}
              <span className="required-asterisk">*</span>
            </label>
            <textarea id="message" name="message" rows="6" value={formData.message} onChange={handleChange} required disabled={isSubmitting}></textarea>
          </div>
          {!IS_PRERENDERING && (
            <Suspense fallback={null}>
              <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={onChange} onErrored={onErrored} onExpired={onExpired} />
            </Suspense>
          )}
          <button type="submit" className="submit-btn" disabled={isSubmitting || IS_PRERENDERING}>
            {isSubmitting ? t('contactUsPage.sendingButton') : t('contactUsPage.sendButton')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactUs;
